import { Container, Graphics, Text } from 'pixi.js';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface FreeSpinChipOptions {
  /** Border + label colour. Defaults to brand pink. */
  accentColor?: number;
  /** Glyph rendered before the count. Defaults to scatter target. */
  glyph?: string;
}

/**
 * Compact pill-shaped chip showing the remaining free-spin count during
 * a bonus round. Hidden when count is zero — host components toggle
 * visibility via {@link setCount}.
 *
 * <p>Sits in the slot HUD between the balance and the machine. The host
 * scene's free-spin model subscription drives it.
 */
export class FreeSpinChip extends Container {
  private readonly bg: Graphics;
  private readonly text: Text;
  private readonly accentColor: number;
  private readonly glyph: string;

  constructor(options: FreeSpinChipOptions = {}) {
    super();
    this.accentColor = options.accentColor ?? 0xff6bd6;
    this.glyph = options.glyph ?? '🎯';
    this.visible = false;

    this.bg = new Graphics();
    this.addChild(this.bg);

    this.text = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 13,
        fontWeight: '900',
        fill: 0xffffff,
        letterSpacing: 1.5,
      },
    });
    this.text.anchor.set(0.5);
    this.addChild(this.text);

    this.draw(0);
  }

  setCount(count: number): void {
    this.visible = count > 0;
    if (count <= 0) return;
    this.draw(count);
  }

  private draw(count: number): void {
    const labelText = `${this.glyph} ${count} FREE SPIN${count === 1 ? '' : 'S'}`;
    this.text.text = labelText;
    const padding = 16;
    const width = this.text.width + padding * 2;
    const height = 26;

    this.bg.clear();
    this.bg
      .roundRect(-width / 2, -height / 2, width, height, height / 2)
      .fill({ color: 0x1a0830, alpha: 0.95 })
      .stroke({ color: this.accentColor, alpha: 0.95, width: 2 });
  }
}
